<template>
  <div>
    <panel>
      <div slot="toolbar">
        <w-button class="btn-light float-right mr-2" text="Wybierz grupy" @click="openColumnsModal" />

        <w-button class="btn-light float-right mr-2" text="Wybierz filtry" @click="openFiltersModal" />
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-hover table-striped w-100 dataTable dtr-inline">
          <thead>
            <tr>
              <th class="pointer" title="Sortuj" @click="setOrder(countColumn)" @keydown="setOrder(countColumn)">
                {{ countColumn.title }}
                <i
                  v-if="countColumn.name === sort.order"
                  class="fal fa-fw"
                  :class="{ 'fa-arrow-down': sort.desc, 'fa-arrow-up': !sort.desc }"
                />
              </th>

              <th
                v-for="(column, columnIndex) of selectedColumns"
                :key="'header_' + columnIndex"
                :style="{ cursor: column.sortable ? 'pointer' : 'default' }"
                :title="column.sortable ? 'Sortuj' : ''"
                @click="setOrder(column)"
                @keydown="setOrder(column)"
              >
                {{ column.title }}
                <i
                  v-if="column.sortBy ? column.sortBy === sort.order : column.name === sort.order"
                  class="fal fa-fw"
                  :class="{ 'fa-arrow-down': sort.desc, 'fa-arrow-up': !sort.desc }"
                />
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, rowIndex) of rows" :key="'row_' + rowIndex">
              <td class="text-center">{{ row.count }}</td>

              <td
                v-for="(column, columnIndex) of selectedColumns"
                :key="'row_' + rowIndex + 'cell_' + columnIndex"
                :class="textPositionClass(column, row)"
              >
                {{ cellText(column, row) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div slot="footer" class="float-right">
        <div>
          <post-form
            text="Pobierz CSV"
            class="btn-light mr-2"
            icon="fas fa-download mr-2"
            :url="csvUrl"
            :fields="statisticsFields"
            :disabled="!rows || !rows.length"
          />

          <post-form
            text="Pobierz PDF"
            class="btn-light"
            icon="fas fa-download mr-2"
            :url="pdfUrl"
            :fields="statisticsFields"
            :disabled="!rows || !rows.length"
          />
        </div>
      </div>
    </panel>

    <checkbox-modal
      title="Wybierz grupy"
      class="modal-big"
      checkbox-class="col-12 col-md-6 col-lg-4"
      :options="modalOptions"
      :selected-options="selectedColumnNames"
      :show="showColumnsModal"
      @close="closeColumnsModal"
      @save="saveColumns"
    />

    <filters-modal
      title="Wybierz filtry"
      class="modal-big"
      :filters="filters"
      :show="showFiltersModal"
      @close="closeFiltersModal"
      @save="saveFilters"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import CheckboxModal from '@/components/modals/CheckboxModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import { DocumentValueCodes } from '@/enums/documentValueCodes.enum';
import { Storage } from '@/enums/storage.enum';
import { notificationsMixin } from '@/mixins/notifications';
import { reportFiltersMixin } from '@/mixins/reportFilters';
import { config } from '@/utils/config';
import { tryParseInt } from '@/utils/number';
import { parseJsonOrDefault } from '@/utils/parseJsonOrDefault';
import { getReportFilters, getReportGroups } from '@/utils/report';

// @vuese
// Pogrupowane statystyki
// @group Reports
export default {
  components: { CheckboxModal, Panel, PostForm },
  mixins: [reportFiltersMixin, notificationsMixin],
  data() {
    const columns = JSON.parse(this.$store.state.setting.settings.REPORT_GROUPS.value);
    columns.forEach(column => (column.resultName = column.name.replace(/\./g, '__')));
    return {
      columns,
      csvUrl: config.csvURL + '/report',
      pdfUrl: config.renderURL + '/pdf/statistics',
      selectedColumns: this.getColumns(),
      showColumnsModal: false,
      sort: this.$route.query ? { order: this.$route.query.order, desc: this.$route.query.desc } : undefined,
      countColumn: { name: 'count', title: 'Liczba złożonych wniosków', sortable: true }
    };
  },
  computed: {
    ...mapState({
      rows: state => state.documentIndex.statistics.result
    }),
    modalOptions() {
      return this.columns.map(column => ({ text: column.title, value: column.name }));
    },
    selectedColumnNames() {
      return this.selectedColumns.map(column => column.name);
    },
    statisticsFields() {
      this.filters; // pola nie odświeżą się po zmianie filtrów bez tego

      const enums = this.selectedColumns
        .filter(column => !!column.enum)
        .reduce((acc, column) => {
          acc[column.resultName] = column.enum;
          return acc;
        }, {});

      const headersMap = this.selectedColumns.reduce((acc, column) => {
        acc[column.resultName] = column.title;
        return acc;
      }, {});
      headersMap.count = this.countColumn.title;
      return [
        { name: 'filters', value: JSON.stringify(getReportFilters(this.$route)) },
        { name: 'groups', value: JSON.stringify(this.selectedColumnNames) },
        { name: 'headersMap', value: JSON.stringify(headersMap) },
        { name: 'enums', value: JSON.stringify(enums) }
      ];
    }
  },
  methods: {
    // @vuese
    // Pobiera kolumny ze storage
    getColumns() {
      const storageColumns = sessionStorage.getItem(Storage.REPORT_GROUPS + this.$route.params.procedureId);
      if (storageColumns) {
        const columns = parseJsonOrDefault(storageColumns);
        if (columns) {
          return columns;
        }
        sessionStorage.removeItem(Storage.REPORT_GROUPS + this.$route.params.procedureId);
      }
      return [];
    },

    // @vuese
    // Klasa przycisku do wyboru kolumny
    columnButtonClass(column) {
      if (this.selectedColumns.some(selectedColumn => selectedColumn.name === column.name)) {
        return 'btn-primary';
      }
      return 'btn-light';
    },

    // @vuese
    // Pobiera raport
    downloadGroups() {
      this.notifySuccess('Pobrano raporty');
    },

    // @vuese
    // Zwraca klasę ustawiającą pozycję tekstu w zależności od typu danych
    textPositionClass(column, row) {
      const value = this.cellText(column, row);
      if (typeof value === 'number') {
        return 'text-right';
      }
      if (typeof value === 'boolean') {
        return 'text-center';
      }
      return 'text-left';
    },

    // @vuese
    // Treść komórki
    cellText(column, row) {
      const value = this.rowValue(column, row);
      if (column.enum) {
        return column.enum[value];
      }
      if (value == null || value === '') {
        return '';
      }
      return value;
    },

    // @vuese
    // Zwraca wartość wiersza dla danej kolumny
    rowValue(column, row) {
      const value = row._id[column.resultName];
      if (column.nameTransformer) {
        if (typeof column.nameTransformer === 'string') {
          return new Function('value', column.nameTransformer)(value);
        }
        return column.nameTransformer(value);
      }
      return value;
    },

    // @vuese
    // Aktualizuje listę
    updateList() {
      getReportGroups(this.$route);
    },

    // @vuese
    // Otwiera modal z wyborem kolumn
    openColumnsModal() {
      this.showColumnsModal = true;
    },

    // @vuese
    // Zamyka modal z wyborem kolumn
    closeColumnsModal() {
      this.showColumnsModal = false;
    },

    // @vuese
    // Zapisuje wybrane kolumny
    saveColumns(columns) {
      this.selectedColumns = this.columns.filter(column => columns.includes(column.name));
      sessionStorage.setItem(Storage.REPORT_GROUPS + this.procedureId, JSON.stringify(this.selectedColumns));
      this.updateList();
      this.closeColumnsModal();
    },

    // @vuese
    // Ustawianie sortowania tabeli
    setOrder(column) {
      if (column.sortable) {
        const desc = this.sort.order === (column.sortBy ?? column.name) ? !this.sort.desc : true;
        this.order({ order: column.sortBy ?? column.name, desc: typeof desc === 'boolean' ? desc : true });
      }
    },

    // @vuese
    // Zmiana sortowania tabeli
    order(order) {
      this.sort = order;
      this.reload({ ...this.$route.query, ...order });
    },

    // @vuese
    // Przeładowuje stronę
    reload(query) {
      const nextRoute = { name: this.$route.name, params: this.$route.params, query };
      this.$router.replace(nextRoute);
    },

    // @vuese
    // Aktualizacja listy i route'a
    updateListAndRouteUpdate(to, next) {
      getReportGroups(to)
        .then(() => next && next())
        .catch(err => this.notifyError('Wystąpił błąd podczas pobierania raportów.'));
    }
  },
  beforeRouteUpdate(to, from, next) {
    this.updateListAndRouteUpdate(to, next);
  }
};
</script>

<style>
.columns .columns-title {
  font-weight: 600;
}

.pointer {
  cursor: pointer;
}
</style>
